import type {
  IntegrationCategory,
  IntegrationDefinition,
  IntegrationId,
} from './types';

/**
 * The single source of truth for supported integrations. Adding one =
 * 1 entry here + 1 backend OAuth provider + icon. The `delivery` spec is
 * resolved into deploy manifests and executed as data by the CLI, so a new
 * MCP integration with no special logic needs no CLI release.
 */
export const INTEGRATION_REGISTRY: Record<IntegrationId, IntegrationDefinition> = {
  jira: {
    // Registry id kept 'jira' for id-stability (existing LinkedIntegration rows
    // stay valid — zero data migration); only the DISPLAY is "Atlassian". The
    // one mcp-atlassian server serves BOTH Jira and Confluence, so the same
    // entry now requests Confluence scopes too.
    id: 'jira',
    name: 'Atlassian',
    icon: 'jira',
    category: 'tracker',
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Jira + Confluence 3LO granular scopes. The Confluence pair
      // (read:confluence-content.all / write:confluence-content) is the set
      // mcp-atlassian's own Authentication docs recommend for full read+write
      // Confluence (matches its documented env scope string).
      scopes: [
        'read:jira-work',
        'write:jira-work',
        'read:confluence-content.all',
        'write:confluence-content',
        'offline_access',
      ],
    },
    delivery: {
      mcp: {
        // mcp-atlassian in BYO-token mode (headless; credentials via env only).
        // Version PINNED to the exact release verified headless by Plan 2's
        // Docker integration test (apps/cli mcp-shim.int.test.ts).
        command: 'uvx',
        args: ['mcp-atlassian==0.22.1'],
        envMapping: {
          ATLASSIAN_OAUTH_ACCESS_TOKEN: 'accessToken',
          ATLASSIAN_OAUTH_CLOUD_ID: 'cloudId',
        },
        // Without ATLASSIAN_OAUTH_ENABLE=true, JiraConfig.from_env() raises
        // "Missing required JIRA_URL" (swallowed at server startup) and the
        // server silently registers ZERO Jira tools. The flag activates
        // mcp-atlassian's "minimal OAuth config for user-provided tokens"
        // mode — the BYO-token path the broker feeds. Static + non-secret.
        staticEnv: { ATLASSIAN_OAUTH_ENABLE: 'true' },
      },
    },
  },
  sentry: {
    id: 'sentry',
    name: 'Sentry',
    icon: 'sentry',
    category: 'observability',
    // LIVE — the Sentry OAuth Application (Confidential) is registered and
    // SENTRY_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager
    // (prod+dev). The backend SentryOAuthProvider is config-gated (503 if
    // env unset) so this is safe even mid-rollout before the secrets mount.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // FULL read+write across every Sentry resource — the agent can read
      // issues/events/projects AND act (resolve/assign issues, manage
      // projects/teams/members, releases). `:write` implies `:read`. Admin
      // (destructive org/member management) is deliberately NOT requested.
      // ⚠️ Changing these requires the user to RE-LINK Sentry — the existing
      // token only carries whatever scopes it was granted at link time.
      scopes: [
        'org:read',
        'org:write',
        'project:read',
        'project:write',
        'team:read',
        'team:write',
        'member:read',
        'member:write',
        'event:read',
        'event:write',
        'project:releases',
      ],
    },
    delivery: {
      mcp: {
        // Sentry's official stdio MCP server (Node). BYO-token headless: the
        // OAuth access token is fed via SENTRY_ACCESS_TOKEN and the host via
        // SENTRY_HOST (never argv — env only). Version PINNED; bump only
        // after re-verifying headless in the mcp-shim integration test.
        command: 'npx',
        // `--add-scopes` widens the server's default READ-ONLY tool surface to
        // include the write tools our OAuth scopes now grant (resolve/assign
        // issue, update project, etc.), so the agent exposes read AND write.
        args: [
          '-y',
          '@sentry/mcp-server@0.18.0',
          '--add-scopes=org:write,project:write,team:write,member:write,event:write',
        ],
        envMapping: {
          SENTRY_ACCESS_TOKEN: 'accessToken',
          SENTRY_HOST: 'host',
        },
      },
    },
  },
  linear: {
    id: 'linear',
    name: 'Linear',
    icon: 'linear',
    category: 'tracker',
    // LIVE — the Linear OAuth Application (Public + Confidential) is registered
    // and LINEAR_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager
    // (prod+dev). The backend LinearOAuthProvider is config-gated (503 if env
    // unset) so this is safe even mid-rollout before the secrets mount.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Linear's coarse scopes: `read` (all issues/projects/comments/cycles)
      // + `write` (create/update issues, comments, state). `write` implies the
      // create/update surface the agent's tools need. `admin` (destructive
      // workspace management) is deliberately NOT requested. ⚠️ Changing these
      // requires the user to RE-LINK Linear — the token only carries the scopes
      // granted at link time. Linear expects a COMMA-separated `scope` param.
      scopes: ['read', 'write'],
    },
    delivery: {
      mcp: {
        // mcp-linear (stdio, @linear/sdk) in BYO-token headless mode: the OAuth
        // access token is fed via LINEAR_API_KEY (env only, never argv) and the
        // Linear GraphQL API accepts it as the Authorization header directly.
        // Verified headless end-to-end (search_issues returned real issues) —
        // the OFFICIAL remote MCP (mcp.linear.app) can't be used here because it
        // forces its own interactive browser OAuth. Version PINNED; bump only
        // after re-verifying headless. Tools: search/get/create/update issue +
        // add comment (read + write).
        command: 'npx',
        args: ['-y', 'mcp-linear@0.1.8'],
        envMapping: {
          LINEAR_API_KEY: 'accessToken',
        },
      },
    },
  },
  github: {
    id: 'github',
    name: 'GitHub',
    icon: 'github',
    category: 'version_control',
    // LIVE. GitHub is the product's code substrate (codespaces + the PR
    // Command Center), and it was historically the ONE connection outside this
    // registry: its credential lives in a `ProviderToken` row rather than the
    // integrations vault, so it was rendered by a hand-written special-case row
    // and could not legally appear in a deploy's `integrationIds` (the manifest
    // resolver rejects unknown ids — a recurring bug class).
    //
    // `kind: 'connection'` closes that gap without re-plumbing OAuth: the entry
    // makes GitHub a first-class, categorised catalog row whose credential the
    // backend resolves from the SAME `ProviderToken` it always used, while the
    // connect/disconnect flow stays owned by the codespaces rail (the clients
    // route those two actions there). ⚠️ It is a REAL connection with a REAL
    // disconnect — do NOT treat it like `github_issues`, which merely derives
    // from it and has no actions of its own.
    enabled: true,
    auth: { kind: 'connection', connection: 'github' },
    // No MCP: a deployed box already has an authenticated `gh` on PATH.
    delivery: {},
  },
  gitlab: {
    id: 'gitlab',
    name: 'GitLab',
    icon: 'gitlab',
    category: 'version_control',
    // LIVE — a user-owned gitlab.com application (Confidential) with BOTH env
    // callbacks registered, so dev/prod share the client and differ only in
    // GITLAB_OAUTH_REDIRECT_URI. The backend GitLabOAuthProvider is
    // config-gated (503 if env unset), so this is safe mid-rollout.
    //
    // ⚠️ Unlike `github`, this is a NORMAL `oauth_redirect` integration: its
    // credential is vaulted here rather than living in a ProviderToken, because
    // nothing else in the product owns a GitLab connection (GitHub's lives on
    // the codespaces rail, which is why it's `kind: 'connection'`).
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // GitLab has NO per-resource scopes — `api` is the only one that grants
      // merge-request WRITE (comment / approve / merge / close), so a
      // read-only alternative would make the whole MR surface useless.
      // `write_repository` is the git-over-HTTPS rail for the agent's push;
      // `api` already covers it for user tokens, but it costs nothing on a
      // consent screen that already says "complete read/write access" and
      // changing scopes later forces EVERY user to re-authorize.
      scopes: ['api', 'write_repository'],
    },
    // No MCP: the box's `git` is authenticated for push, and the MR surface is
    // served backend-side by the VCS provider — same shape as `github`.
    delivery: {},
  },
  github_issues: {
    id: 'github_issues',
    name: 'GitHub Issues',
    icon: 'github_issues',
    category: 'tracker',
    // LIVE, and the ONLY integration with NO link flow of its own: the
    // credential is DERIVED from the GitHub connection the user already made
    // for codespaces/PRs (`ProviderToken` provider='github-codespaces', which
    // carries `repo` scope — enough for the whole Issues surface). So there is
    // no OAuth app, no client id/secret, no Secret Manager entry and nothing
    // to configuration-gate. The backend auto-provisions the catalog row the
    // same way `ensureHouseAgentRow` does for the house agent, and resolves
    // the token live per call rather than vaulting a copy (it can't go stale,
    // and GitHub token refresh stays owned by exactly one place).
    enabled: true,
    auth: { kind: 'derived', derivedFrom: 'github' },
    // NO MCP server on purpose. Every other tracker needs one to give the agent
    // tools, but a deployed box ALREADY has an authenticated `gh` on PATH (the
    // codespace bootstrap exports GH_TOKEN), so `gh issue list/create/comment`
    // works with zero delivery. That also means nothing to pre-warm and no
    // third-party MCP package to pin and keep alive. The Start-from-Work-Item
    // side is served backend-side by the `TrackerProvider`, not by delivery.
    delivery: {},
  },
  slack: {
    id: 'slack',
    name: 'Slack',
    icon: 'slack',
    category: 'comms',
    // LIVE — the Slack app (OAuth v2, USER token — the agent acts AS THE USER)
    // is registered and
    // SLACK_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager
    // (prod+dev). The backend SlackOAuthProvider is config-gated (503 if env
    // unset) so this is safe even mid-rollout before the secrets mount.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Slack USER Token Scopes (OAuth v2). Read + write across channels,
      // groups, DMs: list/read history, post messages, react — all AS THE USER.
      // The backend provider requests these under `user_scope` (comma-separated)
      // and stores the authed_user `xoxp-…` token, so the agent sees everything
      // the user sees (no bot needs to be invited to channels). ⚠️ Changing
      // these requires the user to RE-AUTHORIZE the Slack app.
      scopes: [
        'channels:read',
        'channels:history',
        'groups:read',
        'groups:history',
        'chat:write',
        'reactions:read',
        'reactions:write',
        'users:read',
        'im:read',
        'im:history',
        'mpim:read',
        'mpim:history',
        // Message search across the user's channels/DMs (a user-only scope).
        'search:read',
      ],
    },
    delivery: {
      mcp: {
        // Slack's official reference MCP server (Node). BYO-token headless: the
        // OAuth v2 USER token (xoxp-…) is fed via SLACK_BOT_TOKEN (the server's
        // env var name — it sends whatever token as `Authorization: Bearer`, and
        // Slack's Web API accepts a user token there) and the team id via
        // SLACK_TEAM_ID (env only, never argv). Version PINNED; bump only after
        // re-verifying headless. Tools: list_channels, post_message,
        // reply_to_thread, add_reaction, get_channel_history,
        // get_thread_replies, get_users, get_user_profile (read + write).
        command: 'npx',
        args: ['-y', '@modelcontextprotocol/server-slack@2025.4.25'],
        envMapping: {
          SLACK_BOT_TOKEN: 'accessToken',
          SLACK_TEAM_ID: 'teamId',
        },
      },
    },
  },
  microsoft_teams: {
    id: 'microsoft_teams',
    name: 'Microsoft Teams',
    icon: 'microsoft_teams',
    category: 'comms',
    // LIVE — Microsoft identity platform v2.0 OAuth (multi-tenant, Confidential).
    // The agent reads chats/channels + posts AS THE USER over Microsoft Graph.
    // MS_TEAMS_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager (prod+dev);
    // the backend TeamsOAuthProvider is config-gated (503 if env unset).
    // ⚠️ Chat.* + ChannelMessage.Read.All (`.All`) require TENANT-ADMIN consent
    // (MC1163922, Oct-2025) — the connecting user must be an org admin, or their
    // IT admin approves the app for the tenant. Not a self-serve funnel like Slack.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Delegated Microsoft Graph scopes. `offline_access` → rotating refresh
      // (Teams access tokens are ~1h). ⚠️ Changing these requires the user to
      // RE-LINK — the token carries only the scopes granted at link time.
      scopes: [
        'User.Read',
        'User.ReadBasic.All',
        'Team.ReadBasic.All',
        'Channel.ReadBasic.All',
        'ChannelMessage.Read.All',
        'ChannelMessage.Send',
        'Chat.Read',
        'Chat.ReadWrite',
        'offline_access',
      ],
    },
    delivery: {
      mcp: {
        // @floriscornel/teams-mcp (Node): BYO Graph access token via AUTH_TOKEN
        // (env only, never argv). Headless. Version PINNED; bump only after
        // re-verifying headless. Tools: get/send channel + chat messages, replies.
        command: 'npx',
        args: ['-y', '@floriscornel/teams-mcp@0.9.0'],
        envMapping: {
          AUTH_TOKEN: 'accessToken',
        },
      },
    },
  },
  google_chat: {
    id: 'google_chat',
    name: 'Google Chat',
    icon: 'google_chat',
    category: 'comms',
    // COMING SOON — placeholder catalog entry (no OAuth provider / MCP yet). The
    // agent will post review pings + read spaces AS THE USER over the Google Chat
    // API once the provider lands. `enabled:false` → dimmed "coming soon" tile.
    enabled: false,
    auth: { kind: 'oauth_redirect' },
    delivery: {},
  },
  discord: {
    id: 'discord',
    name: 'Discord',
    icon: 'discord',
    category: 'comms',
    // LIVE — the DiscordOAuthProvider + DISCORD_* secrets (client id/secret,
    // bot token, per-env redirect) are registered; the backend provider is
    // config-gated (503 if env unset). Follows the SLACK pattern (OAuth redirect, zero friction),
    // EXCEPT Discord OAuth gives no per-install token: the user's `bot`-scope
    // authorize INVITES the app's single bot into their guild, and we store the
    // returned guild id as the per-user credential. The broker injects the app's
    // BOT token as `accessToken` (from config), so `DISCORD_TOKEN` = that bot
    // token and `DISCORD_GUILD_ID` = the user's guild.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // `bot` invites the app's bot into the user's guild (Guild Install);
      // `guilds` lets the exchange read the guild name for display. The bot's
      // channel permissions (View Channels + Read Message History + Send
      // Messages + Send Messages in Threads) are set on the app's bot, NOT here.
      // ⚠️ The app's bot MUST have the Message Content privileged intent enabled
      // or read_messages returns empty content.
      scopes: ['bot', 'guilds'],
    },
    delivery: {
      mcp: {
        // mcp-discord (barryyip0625) — Node stdio, BYO bot token headless via the
        // DISCORD_TOKEN env (never argv). Version PINNED; bump only after
        // re-verifying headless. Tools: list/read channels + messages, send
        // message, reply in thread, add reaction. `DISCORD_GUILD_ID` scopes it to
        // the user's invited guild.
        command: 'npx',
        args: ['-y', 'mcp-discord@1.3.4'],
        envMapping: {
          DISCORD_TOKEN: 'accessToken',
          DISCORD_GUILD_ID: 'guildId',
        },
      },
    },
  },
  resend: {
    id: 'resend',
    name: 'Resend',
    icon: 'resend',
    category: 'comms',
    // LIVE — api_key (like Azure DevOps): the user pastes a Resend API key
    // (`re_…`); Resend has NO OAuth, so there's no OAuth app / GSM secret /
    // config-gated 503 — a backend VALIDATOR proves the key against the Resend
    // API and vaults it. ⚠️ SEND-ONLY email → `sendOnly: true` keeps it OUT of
    // From-Conversation (comms conversation sources need readable threads; Resend
    // has none) while still being a linkable comms tool the agent uses via MCP.
    enabled: true,
    sendOnly: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'API Key',
          placeholder: 're_xxxxxxxxxxxxxxxx',
          secret: true,
          help: 'Create in Resend → API Keys (https://resend.com/api-keys). "Sending access" is enough.',
        },
      ],
    },
    delivery: {
      mcp: {
        // The OFFICIAL resend-mcp (Node) in BYO-token mode: the API key is fed
        // via RESEND_API_KEY (env only, never argv). Version PINNED; bump only
        // after re-verifying headless. Tools: send email + contacts/broadcasts/
        // domains.
        command: 'npx',
        args: ['-y', 'resend-mcp@2.6.1'],
        envMapping: {
          RESEND_API_KEY: 'accessToken',
        },
      },
    },
  },
  posthog: {
    id: 'posthog',
    name: 'PostHog',
    icon: 'posthog',
    category: 'observability',
    // LIVE — api_key: the user pastes a PostHog Personal API Key (phx_…, created
    // with the "MCP Server" preset). PostHog's MCP is HOSTED-ONLY
    // (mcp.posthog.com) over HTTP, and its stdio bridge (mcp-remote) forces its
    // own browser OAuth — incompatible with our headless broker. So delivery
    // uses the shim's HTTP transport: it relays to the hosted MCP with the key
    // as a Bearer header (exactly Cursor's {url, headers} config). No OAuth, no
    // GSM secret; a backend VALIDATOR proves the key + vaults it.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'Personal API Key',
          placeholder: 'phx_xxxxxxxxxxxxxxxx',
          secret: true,
          help: 'PostHog → Settings → Personal API keys → create with the "MCP Server" preset (scopes it to a project).',
        },
      ],
    },
    delivery: {
      mcp: {
        // HTTP transport (not a spawned stdio server) — the shim relays to
        // PostHog's hosted MCP with the key as a Bearer header. The key stays
        // server-side of the shim, never on argv.
        command: '',
        args: [],
        envMapping: {},
        httpUrl: 'https://mcp.posthog.com/mcp',
        httpHeaders: { Authorization: 'Bearer {accessToken}' },
      },
    },
  },
  datadog: {
    id: 'datadog',
    name: 'Datadog',
    icon: 'datadog',
    category: 'observability',
    // LIVE — api_key: the user pastes a Datadog API key (org-scoped) + an
    // Application key (user-scoped, grants the read scopes) + their regional
    // site. Datadog's OFFICIAL hosted MCP (mcp.<site>) accepts these as headers
    // headless (verified) — no OAuth, no GSM secret. Delivery uses the shim's
    // HTTP transport with the site templated into the URL and both keys sent as
    // DD-API-KEY / DD-APPLICATION-KEY headers.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'API Key',
          placeholder: 'Datadog API key',
          secret: true,
          help: 'Organization API key — Datadog → Organization Settings → API Keys.',
        },
        {
          key: 'appKey',
          label: 'Application Key',
          placeholder: 'Datadog application key',
          secret: true,
          help: 'User Application key (grants the read scopes) — Datadog → Organization Settings → Application Keys.',
        },
        {
          key: 'host',
          label: 'Site',
          placeholder: 'datadoghq.com',
          secret: false,
          help: 'Your Datadog site — US1: datadoghq.com · EU: datadoghq.eu · US3: us3.datadoghq.com · US5: us5.datadoghq.com · AP1: ap1.datadoghq.com.',
        },
      ],
    },
    delivery: {
      mcp: {
        // HTTP transport — the shim relays to Datadog's hosted MCP for the
        // user's site, with both keys as headers. Keys stay server-side of the
        // shim, never on argv.
        command: '',
        args: [],
        envMapping: {},
        httpUrl: 'https://mcp.{host}/api/unstable/mcp-server/mcp',
        httpHeaders: {
          'DD-API-KEY': '{accessToken}',
          'DD-APPLICATION-KEY': '{appKey}',
        },
      },
    },
  },
  notion: {
    id: 'notion',
    name: 'Notion',
    icon: 'notion',
    category: 'docs',
    // LIVE — the Notion public OAuth integration is registered and
    // NOTION_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager
    // (prod+dev). The backend NotionOAuthProvider is config-gated (503 if env
    // unset) so this is safe even mid-rollout before the secrets mount.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Notion does NOT use per-request OAuth scopes — access is governed by
      // the integration's configured CAPABILITIES (read/update/insert content
      // + read user info), set once on the Notion integration, not passed in
      // the authorize URL. So there is no `scope` param to request here.
      scopes: [],
    },
    delivery: {
      mcp: {
        // Notion's OFFICIAL stdio MCP server (Node). BYO-token headless: the
        // OAuth access token is fed via NOTION_TOKEN and the server sends it as
        // `Authorization: Bearer` + `Notion-Version: 2022-06-28` (env only,
        // never argv). No discriminator — the token alone authenticates its
        // workspace. Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', '@notionhq/notion-mcp-server@2.4.1'],
        envMapping: {
          NOTION_TOKEN: 'accessToken',
        },
      },
    },
  },
  azure_devops: {
    id: 'azure_devops',
    name: 'Azure DevOps',
    icon: 'azure_devops',
    category: 'tracker',
    // LIVE — the FIRST api_key (PAT) integration. No OAuth: Azure DevOps OAuth
    // apps are being sunset by Microsoft in favor of Entra ID, and PATs are the
    // native, reliable ADO auth. The user pastes their org URL + a PAT; the
    // backend validates against the ADO REST API and vaults it. No env secrets
    // to configure (config-gated 503 doesn't apply — there's no OAuth app).
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'orgUrl',
          label: 'Organization URL',
          placeholder: 'https://dev.azure.com/your-org',
          secret: false,
          help: 'Your Azure DevOps organization URL — e.g. https://dev.azure.com/contoso',
        },
        {
          key: 'accessToken',
          label: 'Personal Access Token',
          placeholder: 'Paste your PAT',
          secret: true,
          help: 'Create in Azure DevOps → User settings → Personal access tokens. Recommended scopes: Work Items (Read & Write), Code (Read), Build (Read), Project and Team (Read).',
        },
      ],
    },
    delivery: {
      mcp: {
        // The @tiberriver256 ADO MCP server (Node) in PAT mode: the PAT is fed
        // via AZURE_DEVOPS_PAT (Basic auth) + the org via AZURE_DEVOPS_ORG_URL
        // (env only, never argv). AZURE_DEVOPS_AUTH_METHOD=pat pins the PAT path
        // (the alternative, azure-identity, uses DefaultAzureCredential and
        // would ignore our token). Version PINNED; bump only after re-verifying
        // headless.
        command: 'npx',
        args: ['-y', '@tiberriver256/mcp-server-azure-devops@0.1.46'],
        envMapping: {
          AZURE_DEVOPS_PAT: 'accessToken',
          AZURE_DEVOPS_ORG_URL: 'orgUrl',
        },
        staticEnv: { AZURE_DEVOPS_AUTH_METHOD: 'pat' },
      },
    },
  },
  figma: {
    id: 'figma',
    name: 'Figma',
    icon: 'figma',
    category: 'design',
    // LIVE. OAuth is the PRIMARY flow (app pending Figma's review — until it
    // passes the authorize URL errors "OAuth app doesn't exist"), so a PAT
    // fallback (`apiKeyFallback`) ships alongside it and works TODAY: the user
    // pastes a Figma personal access token. Same OAuth-primary + PAT-secondary
    // shape as GitHub. Flip of the OAuth review is transparent (bead codeagent-m4ix).
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Granular READ-ONLY scopes (legacy `files:read` is deprecated for
      // OAuth). Asset export (GET /v1/images) rides file_content:read.
      // file_variables:read is Enterprise-only and would break linking for
      // normal accounts — deliberately excluded. ⚠️ Changing these requires
      // the user to RE-LINK Figma.
      scopes: [
        'current_user:read',
        'file_content:read',
        'file_metadata:read',
        'file_dev_resources:read',
        'library_content:read',
      ],
      // SECONDARY: paste a Figma personal access token instead of OAuth. The
      // PAT uses the `X-Figma-Token` header, so the delivery env var differs
      // from OAuth (`FIGMA_API_KEY` vs `FIGMA_OAUTH_TOKEN`) — hence its own
      // envMapping, used when the credential was linked via PAT.
      apiKeyFallback: {
        fields: [
          {
            key: 'accessToken',
            label: 'Figma personal access token',
            placeholder: 'figd_…',
            secret: true,
            help: 'Figma → Settings → Security → Personal access tokens (read-only scopes).',
          },
        ],
        envMapping: {
          FIGMA_API_KEY: 'accessToken',
        },
      },
    },
    delivery: {
      mcp: {
        // Framelink figma-developer-mcp (Node, stdio) in BYO-token headless
        // mode — the ONLY known Figma MCP server that accepts an OAuth
        // Bearer token: FIGMA_OAUTH_TOKEN → `Authorization: Bearer` (env
        // only, never argv). Figma's official servers can't be used here
        // (remote = interactive OAuth + client allowlist; Dev Mode =
        // desktop app). Version PINNED; bump only after re-verifying
        // headless. Tools: get_figma_data (condensed layout extraction) +
        // download_figma_images (asset export).
        command: 'npx',
        args: ['-y', 'figma-developer-mcp@0.13.2', '--stdio', '--no-telemetry'],
        envMapping: {
          FIGMA_OAUTH_TOKEN: 'accessToken',
        },
      },
    },
  },
  stitch: {
    id: 'stitch',
    name: 'Stitch',
    icon: 'stitch',
    category: 'design',
    // LIVE — api_key: the user pastes a Stitch API key (Google Stitch →
    // Settings → API Key). Stitch's MCP is hosted-only (stitch.googleapis.com)
    // and accepts the key as the `X-Goog-Api-Key` header headless (verified) —
    // no OAuth, no GSM secret. Delivery uses the shim's HTTP transport.
    // (Stitch ALSO supports Google OAuth 2.0, but that's a follow-up — our model
    // is one auth kind per integration and api_key is the simple, headless path.)
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'API Key',
          placeholder: 'Stitch API key',
          secret: true,
          help: 'Google Stitch → profile menu → Stitch Settings → API Key → Create Key.',
        },
      ],
    },
    delivery: {
      mcp: {
        // HTTP transport — the shim relays to Stitch's hosted MCP with the key
        // as the X-Goog-Api-Key header. Key stays server-side of the shim.
        command: '',
        args: [],
        envMapping: {},
        httpUrl: 'https://stitch.googleapis.com/mcp',
        httpHeaders: { 'X-Goog-Api-Key': '{accessToken}' },
      },
    },
  },
  vercel: {
    id: 'vercel',
    name: 'Vercel',
    icon: 'vercel',
    category: 'infrastructure',
    // DARK — OAuth (PKCE public client). The user authorizes our registered
    // Vercel OAuth app; the access token calls Vercel's OFFICIAL hosted remote
    // MCP (mcp.vercel.com, Streamable HTTP) as a Bearer header — the SAME shim
    // HTTP transport as PostHog/Datadog. Vercel MCP is approved-clients + per-
    // connection OAuth consent, but that consent is the ONE-TIME authorize (not
    // per MCP request), so a backend-brokered access token works headless.
    // Endpoints (from mcp.vercel.com/.well-known/oauth-protected-resource →
    // vercel.com/.well-known/oauth-authorization-server): authorize
    // https://vercel.com/oauth/authorize, token
    // https://vercel.com/api/login/oauth/token, PKCE S256, token-endpoint auth
    // 'none' (public). LIVE — the headless Bearer against mcp.vercel.com was
    // verified (2026-07-27).
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      scopes: ['openid', 'offline_access'],
    },
    delivery: {
      mcp: {
        command: '',
        args: [],
        envMapping: {},
        httpUrl: 'https://mcp.vercel.com',
        httpHeaders: { Authorization: 'Bearer {accessToken}' },
      },
    },
  },
  trello: {
    id: 'trello',
    name: 'Trello',
    icon: 'trello',
    category: 'tracker',
    // LIVE — api_key rail (like Azure DevOps). Trello's auth is a developer API
    // key + a user token, NOT an OAuth2 code exchange: its token returns via a
    // URL fragment / OAuth 1.0a, incompatible with our server-side broker. The
    // user pastes both; the backend validates against the Trello REST API and
    // vaults them. No OAuth app, no GSM secrets, no config-gated 503.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'apiKey',
          label: 'API Key',
          placeholder: 'Your Trello API key',
          secret: false,
          help: 'Create a Power-Up at https://trello.com/power-ups/admin and copy its API key.',
        },
        {
          key: 'accessToken',
          label: 'Token',
          placeholder: 'Your Trello token',
          secret: true,
          help: 'On the API-key page, click the "Token" link to authorize + generate a token (scope read,write, never-expiring).',
        },
      ],
    },
    delivery: {
      mcp: {
        // mcp-server-trello (Node): TRELLO_API_KEY + TRELLO_TOKEN via env only,
        // never argv. Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', 'mcp-server-trello@1.0.4'],
        envMapping: {
          TRELLO_API_KEY: 'apiKey',
          TRELLO_TOKEN: 'accessToken',
        },
      },
    },
  },
  clickup: {
    id: 'clickup',
    name: 'ClickUp',
    icon: 'clickup',
    category: 'tracker',
    // DARK — OAuth 2.0 (authorization code). ClickUp's token DOES NOT expire and
    // has NO refresh token, and its OAuth has NO granular scopes (the app gets
    // the workspaces the user authorizes). enabled:false until the OAuth app is
    // registered (client_id/secret) + secrets land + Step-8 live-verify.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      scopes: [],
    },
    delivery: {
      mcp: {
        // @taazkareem/clickup-mcp-server (Node): CLICKUP_API_KEY (the OAuth
        // access token — ClickUp accepts it in the Authorization header) +
        // CLICKUP_TEAM_ID (the workspace id, captured at exchange) via env only.
        // Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', '@taazkareem/clickup-mcp-server@0.14.4'],
        envMapping: {
          CLICKUP_API_KEY: 'accessToken',
          CLICKUP_TEAM_ID: 'teamId',
        },
      },
    },
  },
  n8n: {
    id: 'n8n',
    name: 'n8n',
    icon: 'n8n',
    category: 'automation',
    // LIVE — api_key rail (like Trello / Azure DevOps). n8n auth is an API key
    // (Settings → n8n API) + the instance URL (n8n.cloud or self-hosted). The
    // user pastes both; the backend validates against the n8n REST API + vaults
    // them. No OAuth app, no GSM secrets, no config-gated 503.
    // ⚠️ The instance MUST be PUBLICLY reachable (n8n.cloud or a public self-host)
    // — a localhost/private n8n can't be reached from the deploy box.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'instanceUrl',
          label: 'Instance URL',
          placeholder: 'https://your-workspace.app.n8n.cloud',
          secret: false,
          help: 'Your n8n base URL — n8n.cloud or your public self-hosted instance.',
        },
        {
          key: 'accessToken',
          label: 'API Key',
          placeholder: 'n8n_api_…',
          secret: true,
          help: 'Create in n8n → Settings → n8n API → Create an API key.',
        },
      ],
    },
    delivery: {
      mcp: {
        // n8n-mcp (Node): N8N_API_URL + N8N_API_KEY via env only, never argv.
        // Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', 'n8n-mcp@2.66.2'],
        envMapping: {
          N8N_API_URL: 'instanceUrl',
          N8N_API_KEY: 'accessToken',
        },
        // stdio transport (the CLI mcp-shim spawns it over stdio, not HTTP).
        staticEnv: { MCP_MODE: 'stdio' },
      },
    },
  },
  postman: {
    id: 'postman',
    name: 'Postman',
    icon: 'postman',
    category: 'api_tools',
    // LIVE — api_key rail. Postman auth is a personal API key (Settings → API
    // keys). The user pastes it; the backend validates via GET /me + vaults it.
    // No OAuth (Postman's API has no user OAuth), no secrets, no config-503.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'API Key',
          placeholder: 'PMAK-…',
          secret: true,
          help: 'Postman → Settings → API keys → Generate API Key.',
        },
      ],
    },
    delivery: {
      mcp: {
        // Postman's OFFICIAL stdio MCP server (Node). BYO key via POSTMAN_API_KEY
        // (env only). Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', '@postman/postman-mcp-server@2.11.2'],
        envMapping: {
          POSTMAN_API_KEY: 'accessToken',
        },
      },
    },
  },
  mixpanel: {
    id: 'mixpanel',
    name: 'Mixpanel',
    icon: 'mixpanel',
    category: 'analytics',
    // LIVE — api_key rail. Mixpanel query/export auth is a SERVICE ACCOUNT
    // (username + password, Basic auth) scoped to a project (project id). The
    // user pastes all three; the backend validates + vaults them. No OAuth
    // redirect (service accounts are machine-to-machine), no GSM secrets.
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'Service Account Username',
          placeholder: 'your-sa.mp-service-account',
          secret: false,
          help: 'Mixpanel → Organization Settings → Service Accounts → create one with project access.',
        },
        {
          key: 'secretKey',
          label: 'Service Account Secret',
          placeholder: 'the service account secret',
          secret: true,
          help: 'Shown once when you create the service account — copy it then.',
        },
        {
          key: 'projectId',
          label: 'Project ID',
          placeholder: '1234567',
          secret: false,
          help: 'Mixpanel → Project Settings → Project ID.',
        },
      ],
    },
    delivery: {
      mcp: {
        // @mendeel/mcp-mixpanel (Node): the service account + project id via env
        // only (MIXPANEL_SERVICE_ACCOUNT_USERNAME/PASSWORD + MIXPANEL_PROJECT_ID).
        // Version PINNED; bump only after re-verifying headless. Default region US.
        command: 'npx',
        args: ['-y', '@mendeel/mcp-mixpanel@1.0.4'],
        envMapping: {
          MIXPANEL_SERVICE_ACCOUNT_USERNAME: 'accessToken',
          MIXPANEL_SERVICE_ACCOUNT_PASSWORD: 'secretKey',
          MIXPANEL_PROJECT_ID: 'projectId',
        },
      },
    },
  },
  supabase: {
    id: 'supabase',
    name: 'Supabase',
    icon: 'supabase',
    category: 'database',
    // LIVE — OAuth 2.0 (authorization code, Confidential). authorize
    // api.supabase.com/v1/oauth/authorize; token /v1/oauth/token
    // (form-urlencoded, client creds via HTTP Basic); rotating refresh tokens.
    // The OAuth app (Organization → OAuth Apps) is registered and
    // SUPABASE_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI are in Secret Manager
    // (prod+dev); the config-gated 503 keeps this safe mid-rollout.
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // ⚠️ Supabase scopes are configured ON THE OAUTH APP, NOT sent in the
      // authorize URL — this list is informational (what the app was granted).
      scopes: ['all'],
    },
    delivery: {
      mcp: {
        // Supabase's OFFICIAL MCP server (Node). The OAuth access token IS a
        // Management API token → fed via SUPABASE_ACCESS_TOKEN (env only, never
        // argv). Version PINNED; bump only after re-verifying headless.
        command: 'npx',
        args: ['-y', '@supabase/mcp-server-supabase@0.9.0'],
        envMapping: {
          SUPABASE_ACCESS_TOKEN: 'accessToken',
        },
      },
    },
  },
  convex: {
    id: 'convex',
    name: 'Convex',
    icon: 'convex',
    category: 'database',
    // LIVE — api_key rail (the user pastes a Convex DEPLOY KEY) + a BUILT-IN MCP.
    // ⚠️ Convex's OWN `convex mcp start` server CANNOT work headlessly: it
    // categorically requires an INTERACTIVE `npx convex dev`/`login`
    // personal-access-token and REJECTS every headless credential — OAuth token,
    // dev/prod deploy key, self-hosted admin key, with every flag
    // (--deployment/--prod/--project-dir) — all return "Not Authorized: Run
    // `npx convex dev` to login" (verified LIVE, exhaustively, 2026-08-03; it
    // also HANGS on the failed auth = the mareado/no-Stop wedge). BUT the
    // deployment's own HTTP admin API accepts the deploy key directly
    // (`Authorization: Convex <deployKey>` → 200; verified). So we serve Convex's
    // tools ourselves via a BUILT-IN MCP (delivery.builtin) against that admin
    // API — see apps/cli/src/integrations/convex-admin-mcp.ts. The user still
    // pastes a deploy key (Dashboard → Project Settings → Deploy Keys).
    enabled: true,
    auth: {
      kind: 'api_key',
      fields: [
        {
          key: 'accessToken',
          label: 'Convex deploy key',
          placeholder: 'prod:happy-animal-123|eyJ2…',
          secret: true,
          help: 'Convex Dashboard → Project Settings → Deploy Keys → Generate a deploy key.',
        },
      ],
    },
    delivery: {
      mcp: {
        // BUILT-IN MCP: the CLI serves Convex's tools itself against the
        // deployment admin REST API with the brokered deploy key (Convex's own
        // `convex mcp start` rejects all headless creds — see the note above).
        // No spawned child, no npx. command/args/envMapping are empty.
        builtin: 'convex-admin',
        command: '',
        args: [],
        envMapping: {},
      },
    },
  },
  confluence: {
    id: 'confluence',
    name: 'Confluence',
    icon: 'confluence',
    category: 'docs',
    // Confluence is served by the SAME Atlassian OAuth + the SAME `mcp-atlassian`
    // server as Jira (the `jira` entry already requests Confluence scopes). This
    // entry is a DISPLAY ALIAS so Confluence is explicit in the Knowledge tab: it
    // BORROWS jira's vaulted credential (`aliasOf: 'jira'`) and has no link flow of
    // its own — connecting Atlassian (the Jira/Atlassian card) lights it up, and
    // the backend resolves its token by delegating to `jira`.
    enabled: true,
    auth: { kind: 'derived', aliasOf: 'jira' },
    delivery: {
      mcp: {
        // Same mcp-atlassian server + BYO-token env as Jira — it exposes the
        // Confluence tools when the deploy selects Confluence without Jira. If
        // BOTH are selected the deploy manifest dedupes identical MCP specs.
        command: 'uvx',
        args: ['mcp-atlassian==0.22.1'],
        envMapping: {
          ATLASSIAN_OAUTH_ACCESS_TOKEN: 'accessToken',
          ATLASSIAN_OAUTH_CLOUD_ID: 'cloudId',
        },
        staticEnv: { ATLASSIAN_OAUTH_ENABLE: 'true' },
      },
    },
  },
  cloudflare: {
    id: 'cloudflare',
    name: 'Cloudflare',
    icon: 'cloudflare',
    category: 'infrastructure',
    // LIVE — Cloudflare self-managed OAuth (dash.cloudflare.com OAuth server,
    // the same one Wrangler uses). Confidential (Client Secret POST). The agent
    // inspects/deploys Workers, Pages, DNS, KV, R2 + reads analytics/logs on the
    // user's account. CLOUDFLARE_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI in Secret
    // Manager (prod+dev); the backend CloudflareOAuthProvider is config-gated (503).
    enabled: true,
    auth: {
      kind: 'oauth_redirect',
      // Cloudflare OAuth scope IDs == API-token permission-group names (dot
      // form, e.g. `workers-scripts.write`). ⚠️ MUST exactly match the scopes
      // configured on the OAuth client (b2b4b87e…). `offline_access` → refresh.
      scopes: [
        // Developer Platform (Workers / Pages / KV / R2 / D1 / observability)
        'd1.read',
        'page.read',
        'page.write',
        'workers-ci.read',
        'workers-ci.write',
        'containers.read',
        'containers.write',
        'workers-kv-storage.read',
        'workers-kv-storage.write',
        'workers-observability.read',
        'workers-observability-telemetry.write',
        'workers-observability.write',
        'r2-catalog.read',
        'r2-catalog.write',
        'r2-catalog-sql.read',
        'workers-r2-bucket-item.read',
        'workers-r2-bucket-item.write',
        'workers-r2.read',
        'workers-r2.write',
        'workers-routes.read',
        'workers-routes.write',
        'workers-scripts.read',
        'workers-scripts.write',
        'workers-tail.read',
        // DNS & Zones
        'account-dns-settings.read',
        'account-dns-settings.write',
        'dns-firewall.read',
        'dns-firewall.write',
        'dns.read',
        'dns-view.read',
        'dns.write',
        'registrar-domains.admin',
        'registrar-sandbox-domains.admin',
        'zone-dns-settings.read',
        'zone-dns-settings.write',
        'zone.read',
        'zone-settings.read',
        'zone-settings.write',
        // Analytics & Logs
        'account-analytics.read',
        'analytics.read',
        'account-logs.read',
        'logs.read',
        'radar.read',
        // Account & Billing
        'account-api-gateway.read',
        'account-settings.read',
        'memberships.read',
        'notifications.read',
        'user-details.read',
        'offline_access',
      ],
    },
    delivery: {
      mcp: {
        // Headless stdio Cloudflare MCP (Cloudflare API v4) — BYO token via env,
        // the OAuth access token works as a Bearer against api.cloudflare.com.
        // The account id is the discriminator for account-level ops. PINNED.
        command: 'npx',
        args: ['-y', '@itunified.io/mcp-cloudflare@2026.4.10-1'],
        envMapping: {
          CLOUDFLARE_API_TOKEN: 'accessToken',
          CLOUDFLARE_ACCOUNT_ID: 'accountId',
        },
      },
    },
  },
};

export function getEnabledIntegrations(): IntegrationDefinition[] {
  return Object.values(INTEGRATION_REGISTRY).filter((m) => m.enabled);
}

export function getIntegration(id: IntegrationId): IntegrationDefinition {
  const meta = INTEGRATION_REGISTRY[id];
  if (!meta) throw new Error(`Unknown integration id: ${id}`);
  return meta;
}

export function isKnownIntegrationId(id: string): id is IntegrationId {
  return id in INTEGRATION_REGISTRY;
}

export function getIntegrationsByCategory(
  category: IntegrationCategory,
): IntegrationDefinition[] {
  return Object.values(INTEGRATION_REGISTRY).filter(
    (m) => m.category === category && m.enabled,
  );
}
